import React, {
  ChangeEvent,
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import Input, { InputProps, InputRef } from "antd/es/input/Input";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import { useConfigProvider } from "../ConfigProvider";

import ErrorRow from "./ErrorRow";
import Labeled, { LabeledProps } from "./Labeled";

export interface TextFieldProps
  extends Omit<InputProps, "onChange">,
    Omit<LabeledProps, "children"> {
  caption?: React.ReactNode;
  className?: string;
  error?: ReactNode;
  locked?: boolean;
  lockTooltip?: string;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  value?: string;
}

const block = bem("mmc-text-field");

const TextField = forwardRef<TextFieldComponent, TextFieldProps>(
  (
    {
      caption,
      className,
      colProps,
      disabled,
      error,
      extra,
      label,
      labelClassName,
      labelPosition,
      locked,
      lockTooltip,
      maxLength,
      onChange,
      required,
      rowProps,
      sideLabelSpan,
      size = "large",
      suffix,
      value,
      ...props
    },
    ref
  ) => {
    const configProvider = useConfigProvider();

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => onChange && onChange(event.target.value, event),
      [onChange]
    );

    const formattedError = useMemo(
      () =>
        maxLength && (value ?? "").length >= maxLength
          ? configProvider.formatMessage("ui.textField.error.limit")
          : error,
      [configProvider, error, maxLength, value]
    );

    const inputRef = useRef<InputRef>(null);
    useImperativeHandle(ref, () => ({
      blur: () => inputRef.current?.blur(),
      focus: () => inputRef.current?.focus(),
    }));

    return (
      <Labeled
        className={cn(block({ disabled: disabled || locked }), className)}
        colProps={colProps}
        extra={extra}
        label={label}
        labelClassName={cn(block("label"), labelClassName)}
        labelPosition={labelPosition}
        required={required}
        rowProps={rowProps}
        sideLabelSpan={sideLabelSpan}
      >
        <div className={block("input")}>
          <Input
            disabled={disabled || locked}
            maxLength={maxLength}
            onChange={handleChange}
            ref={inputRef}
            required={required}
            size={size}
            suffix={
              locked ? (
                <div className={block("lock")}>
                  <Tooltip title={lockTooltip ?? null}>
                    <FontAwesomeIcon icon={faLock} size="xs" />
                  </Tooltip>
                </div>
              ) : (
                suffix
              )
            }
            value={value}
            {...props}
          />
          {formattedError ? (
            <ErrorRow>{formattedError}</ErrorRow>
          ) : caption ? (
            <div className={block("caption")}>{caption}</div>
          ) : null}
        </div>
      </Labeled>
    );
  }
);

export interface TextFieldComponent {
  blur(): void;
  focus: () => void;
}

TextField.displayName = "TextField";

export default TextField;
